import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Video, MessageCircle, Sparkles, Calendar as CalIcon, Check, X, CreditCard, FileText } from "lucide-react";
import PrescriptionDialog from "@/components/PrescriptionDialog";
import AvailabilityManager from "@/components/AvailabilityManager";
import PrescriberProfile from "@/components/PrescriberProfile";
import PricingSettings from "@/components/PricingSettings";

const fmt = (iso) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" });

const StatusBadge = ({ status, paid }) => {
  const map = {
    scheduled: { label: paid ? "Aguardando confirmação" : "Aguardando pagamento", cls: paid ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700" },
    confirmed: { label: "Confirmada", cls: "bg-teal-100 text-teal-800" },
    completed: { label: "Concluída", cls: "bg-navy-900 text-white" },
    cancelled: { label: "Cancelada", cls: "bg-red-100 text-red-800" },
  };
  const s = map[status] || map.scheduled;
  return <span className={`text-xs px-2 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState("");
  const [presc, setPresc] = useState(null);

  const load = useCallback(() => api.get("/appointments").then((r) => setAppts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") {
      toast.success("Google Agenda conectado! ✓");
      window.history.replaceState({}, "", "/dashboard");
    }
  }, []);

  const payNow = async (appt) => {
    try {
      const { data } = await api.post("/payments/checkout", { appointment_id: appt.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/appointments/${id}`, { status });
    toast.success("Consulta atualizada");
    load();
  };
  const saveNote = async () => {
    await api.patch(`/appointments/${editing.id}`, { professional_notes: note });
    toast.success("Anotação salva");
    setEditing(null);
    load();
  };

  const upcoming = appts.filter((a) => a.status !== "completed" && a.status !== "cancelled");
  const past = appts.filter((a) => a.status === "completed" || a.status === "cancelled");

  return (
    <div className="container-optia py-10 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-slate-500">Olá,</p>
          <h1 className="font-display text-3xl font-semibold text-navy-900" data-testid="dash-greeting">{user?.name}</h1>
          <p className="text-slate-600 mt-1">{user?.role === "patient" ? "Como você está hoje?" : "Aqui estão seus pacientes de hoje."}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/aurora"><Button variant="outline" className="rounded-xl border-teal-500 text-teal-700 hover:bg-teal-50" data-testid="dash-aurora-btn"><Sparkles className="w-4 h-4 mr-2" />Falar com Aurora</Button></Link>
          {user?.role === "patient" && <Link to="/profissionais"><Button className="bg-teal-500 hover:bg-teal-600 rounded-xl" data-testid="dash-book-btn">Agendar consulta</Button></Link>}
        </div>
      </div>

      {user?.role === "patient" && !user?.pre_anamnesis_completed && (
        <Link to="/pre-anamnese" className="block mt-6" data-testid="preana-banner">
          <div className="rounded-2xl p-5 bg-amber-50 border border-amber-200 hover:border-amber-400 transition flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-amber-100 grid place-items-center text-amber-700 shrink-0"><FileText className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-medium text-navy-900">Complete sua pré-consulta antes de agendar</div>
              <p className="text-sm text-slate-700 mt-1">Um formulário curto para nossa equipe se preparar melhor para você. Leva ~10 min. <b className="text-teal-700">Clique aqui →</b></p>
            </div>
          </div>
        </Link>
      )}

      {user?.role === "professional" && (
        <div className="mt-8 space-y-6"><PricingSettings /><AvailabilityManager userId={user.id} /><PrescriberProfile /></div>
      )}

      <h2 className="font-display text-xl font-semibold text-navy-900 mt-10">Próximas consultas</h2>      <div className="grid gap-4 mt-4" data-testid="dash-upcoming">
        {upcoming.length === 0 && <p className="text-slate-500">Nada agendado no momento.</p>}
        {upcoming.map((a) => (
          <Card key={a.id} className="card-soft">
            <CardContent className="p-6 flex flex-wrap items-center gap-4 justify-between">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-teal-100 grid place-items-center text-teal-700"><CalIcon className="w-5 h-5" /></div>
                <div>
                  <div className="font-medium text-navy-900">{user?.role === "patient" ? a.professional_name : a.patient_name}</div>
                  <div className="text-sm text-slate-600">{a.professional_specialty}</div>
                  <div className="text-sm text-slate-500 mt-1">{fmt(a.scheduled_at)}</div>
                  {a.reason && <div className="text-xs text-slate-500 mt-1 max-w-md">&quot;{a.reason}&quot;</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={a.status} paid={a.paid} />
                {a.status === "scheduled" && !a.paid && user?.role === "patient" && (
                  <Button size="sm" className="bg-teal-500 hover:bg-teal-600 rounded-lg" onClick={()=>payNow(a)} data-testid={`pay-${a.id}`}><CreditCard className="w-4 h-4 mr-1" />Pagar agora</Button>
                )}
                {a.status === "confirmed" && a.meeting_link && (
                  <a href={a.meeting_link} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-teal-500 hover:bg-teal-600 rounded-lg" data-testid={`join-${a.id}`}><Video className="w-4 h-4 mr-1" />Entrar na sala</Button>
                  </a>
                )}
                {user?.role === "professional" && (a.status === "confirmed" || a.status === "completed") && (
                  <>
                    <Link to={`/prontuario/${a.patient_id}`}><Button size="sm" variant="outline" data-testid={`chart-${a.id}`}><FileText className="w-4 h-4 mr-1" />Prontuário</Button></Link>
                    <Button size="sm" variant="outline" onClick={()=>setPresc(a)} data-testid={`prescribe-${a.id}`}><FileText className="w-4 h-4 mr-1" />Prescrever</Button>
                  </>
                )}
                {user?.role === "professional" && a.status === "confirmed" && (
                  <>
                    <Button size="sm" variant="outline" onClick={()=>{ setEditing(a); setNote(a.professional_notes || ""); }} data-testid={`note-${a.id}`}>Anotações</Button>
                    <Button size="sm" variant="outline" onClick={()=>updateStatus(a.id, "completed")} data-testid={`complete-${a.id}`}><Check className="w-4 h-4" /></Button>
                  </>
                )}
                {a.status === "scheduled" && (
                  <Button size="sm" variant="outline" onClick={()=>updateStatus(a.id, "cancelled")} data-testid={`cancel-${a.id}`}><X className="w-4 h-4 mr-1" />Cancelar</Button>
                )}
                <Link to={`/mensagens/${user?.role === "patient" ? a.professional_id : a.patient_id}`}>
                  <Button size="sm" variant="ghost" data-testid={`msg-${a.id}`}><MessageCircle className="w-4 h-4" /></Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {past.length > 0 && (
        <>
          <h2 className="font-display text-xl font-semibold text-navy-900 mt-10">Histórico</h2>
          <div className="grid gap-3 mt-4" data-testid="dash-past">
            {past.map((a) => (
              <Card key={a.id} className="card-soft opacity-90">
                <CardContent className="p-5 flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <div className="font-medium text-navy-900">{user?.role === "patient" ? a.professional_name : a.patient_name} — {fmt(a.scheduled_at)}</div>
                    {a.professional_notes && <div className="text-sm text-slate-600 mt-1">Nota: {a.professional_notes}</div>}
                  </div>
                  <StatusBadge status={a.status} paid={a.paid} />
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={()=>setEditing(null)}>
          <Card className="max-w-lg w-full" onClick={(e)=>e.stopPropagation()}>
            <CardContent className="p-6">
              <h3 className="font-display text-lg font-semibold text-navy-900">Anotações da sessão</h3>
              <p className="text-xs text-slate-500 mb-3">Registro clínico privado.</p>
              <Textarea value={note} onChange={(e)=>setNote(e.target.value)} rows={8} data-testid="note-textarea" />
              <div className="flex gap-2 justify-end mt-4">
                <Button variant="outline" onClick={()=>setEditing(null)}>Cancelar</Button>
                <Button className="bg-teal-500 hover:bg-teal-600" onClick={saveNote} data-testid="note-save">Salvar</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <PrescriptionDialog open={!!presc} onOpenChange={(o)=>!o && setPresc(null)} appointment={presc} />
    </div>
  );
}
